/**
 * Einen Auszug einem Mandanten zuordnen. Als Fenster in der Bildschirmmitte,
 * mit Suche statt Klappliste, und mit der Moeglichkeit, den Mandanten gleich
 * hier anzulegen, wenn es ihn noch nicht gibt.
 *
 * Mit karte laeuft dasselbe Fenster fuer eine Kreditkartenabrechnung: die
 * Karte statt der IBAN merken, ein Vorschlag ueber die Endziffern waehlt den
 * Mandanten vor, zugeordnet wird ueber die Kartenroute. Ohne karte bleibt
 * alles wie bei den Kontoauszuegen.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type Kartenansicht, type MandantZeile } from '../lib/api'
import { iban as ibanLesbar } from '../lib/format'
import { artParameter } from '../lib/art'
import { endeKurz } from '../lib/karte'
import { Fenster } from './Fenster'
import { Filterfeld } from './Filterfeld'

interface Eigenschaften {
  offen: boolean
  aufSchliessen: () => void
  auszugId: string
  iban: string | null
  inhaber: string | null
  /** Nur bei einer Kreditkartenabrechnung. */
  karte?: { auszugId: string } | undefined
}

export function MandantWaehlen({ offen, aufSchliessen, auszugId, iban, inhaber, karte }: Eigenschaften) {
  const abfragen = useQueryClient()
  const [suche, setzeSuche] = useState('')
  const [gewaehlt, setzeGewaehlt] = useState<string | null>(null)
  const [merken, setzeMerken] = useState(true)
  // Der Kontoinhaber vom Auszug ist der naheliegende Name fuer einen neuen Mandanten.
  const [neuerName, setzeNeuerName] = useState(inhaber ?? '')
  const [fehler, setzeFehler] = useState<string | null>(null)
  // Ist ein neuer Mandant schon angelegt und scheitert danach das Zuordnen,
  // wird beim zweiten Versuch kein zweiter mit demselben Namen angelegt.
  const angelegt = useRef<{ name: string; id: string } | null>(null)

  const liste = useQuery({
    queryKey: ['mandanten', suche],
    queryFn: () => api.holen<{ mandanten: MandantZeile[] }>(`/api/mandanten${suche ? `?suche=${encodeURIComponent(suche)}` : ''}`),
    enabled: offen && !karte,
  })
  // Kartenzweig: eigene Liste mit den Zaehlern der Kreditkarten, dazu Kennung und Vorschlag der Abrechnung.
  const kartenliste = useQuery({
    queryKey: ['mandanten', 'kreditkarte', suche],
    queryFn: () => api.holen<{ mandanten: MandantZeile[] }>(`/api/mandanten?${artParameter('kreditkarte')}${suche ? `&suche=${encodeURIComponent(suche)}` : ''}`),
    enabled: offen && Boolean(karte),
  })
  const kartenansicht = useQuery({
    queryKey: ['kartenabrechnung', karte?.auszugId],
    queryFn: () => api.holen<Kartenansicht>(`/api/karten/abrechnung/${karte!.auszugId}`),
    enabled: offen && Boolean(karte),
  })
  const vorschlag = kartenansicht.data?.vorschlag ?? null
  const kartenKennung = kartenansicht.data?.abrechnung?.kennung ?? null
  const kartenEnde = kartenansicht.data?.abrechnung?.endziffern ?? null
  const mandanten = karte ? kartenliste : liste

  /* Ein Vorschlag ueber die Endziffern waehlt den Mandanten vor. Zugeordnet
     wird erst, wenn ein Mensch auf Zuordnen drueckt. */
  useEffect(() => {
    if (!karte || !vorschlag || gewaehlt) return
    setzeGewaehlt(vorschlag.mandantId)
    setzeNeuerName('')
    // Nur beim ersten Laden des Vorschlags, nicht nach jeder Abwahl.
  }, [vorschlag?.mandantId])

  /* Gibt es schon einen Mandanten, der genau so heisst wie der Kontoinhaber,
     ist er vorgewaehlt. Nur bei exakt gleichem Namen: aehnlich ist ein
     Vorschlag, den ein Mensch bestaetigen soll. */
  useEffect(() => {
    if (!inhaber || gewaehlt || !mandanten.data) return
    const gleich = mandanten.data.mandanten.find((m) => m.name.trim().toLowerCase() === inhaber.trim().toLowerCase())
    if (gleich) {
      setzeGewaehlt(gleich.id)
      setzeNeuerName('')
    }
  }, [mandanten.data, inhaber, gewaehlt])

  const zuordnen = useMutation({
    mutationFn: async () => {
      let mandantId = gewaehlt
      if (!mandantId && neuerName.trim()) {
        const name = neuerName.trim()
        if (angelegt.current?.name !== name) {
          angelegt.current = { name, id: (await api.senden<{ id: string }>('/api/mandanten', { name })).id }
        }
        mandantId = angelegt.current.id
      }
      if (!mandantId) throw new ApiFehler(400, 'Bitte einen Mandanten wählen oder einen Namen für einen neuen eingeben.')
      if (karte) {
        // Vorschlag angenommen: die Abrechnung haengt an dieser hinterlegten Karte.
        const angenommen = vorschlag && vorschlag.mandantId === mandantId
        await api.senden(`/api/karten/abrechnung/${karte.auszugId}/zuordnen`, angenommen
          ? { mandantId, kreditkarteId: vorschlag.kreditkarteId, karteMerken: false }
          : { mandantId, karteMerken: merken })
        return
      }
      await api.senden(`/api/auszuege/${auszugId}/zuordnen`, { mandantId, ibanMerken: merken })
    },
    onSuccess: async () => {
      await abfragen.invalidateQueries()
      schliessen()
    },
    onError: (problem) => setzeFehler(problem instanceof ApiFehler ? problem.message : 'Die Zuordnung hat nicht geklappt.'),
  })

  function schliessen() {
    setzeSuche('')
    setzeGewaehlt(null)
    setzeNeuerName(inhaber ?? '')
    setzeFehler(null)
    setzeMerken(true)
    angelegt.current = null
    aufSchliessen()
  }

  function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    zuordnen.mutate()
  }

  return (
    <Fenster titel="Mandant zuordnen" offen={offen} aufSchliessen={schliessen}>
      <form className="formular" onSubmit={absenden}>
        {fehler && <div className="fehler">{fehler}</div>}

        {!karte && (
        <div className="leiser">
          Konto <span className="zahl">{ibanLesbar(iban)}</span>
          {inhaber ? `, Inhaber laut Auszug: ${inhaber}` : ''}
        </div>
        )}
        {karte && (
          <div className="leiser">
            {kartenansicht.data?.kennungLesbar || 'Karte nicht erkannt'}
            {inhaber ? `, Karteninhaber laut Abrechnung: ${inhaber}` : ''}
          </div>
        )}
        {karte && vorschlag && (
          <div className="hinweis">Diese Endziffern sind bei {vorschlag.mandantName} hinterlegt. Bitte prüfen.</div>
        )}

        {/* Enter im Suchfeld darf das Formular nicht abschicken: das hiesse
            "neuen Mandanten anlegen", und zwar mit dem Kontoinhaber als Namen. */}
        <div onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault() }}>
          <Filterfeld className="suchfeld-voll" wert={suche} setzeWert={setzeSuche} beschriftung="Mandanten suchen" platzhalter="Name, Nummer oder IBAN" />
        </div>

        <div className="wahlliste" role="listbox" aria-label="Mandanten">
          {mandanten.isPending && <div className="leiser">Wird geladen …</div>}
          {mandanten.data?.mandanten.length === 0 && <div className="leiser">Kein Mandant gefunden.</div>}
          {mandanten.data?.mandanten.map((m) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={gewaehlt === m.id}
              className="wahlzeile"
              onClick={() => {
                setzeGewaehlt(m.id)
                setzeNeuerName('')
              }}
            >
              <span>{m.name}</span>
              {m.mandantennummer !== null && <span className="leiser zahl">{m.mandantennummer}</span>}
            </button>
          ))}
        </div>

        <div>
          <label htmlFor="mw-neu">Oder neuen Mandanten anlegen</label>
          <input
            id="mw-neu"
            type="text"
            autoComplete="off"
            data-1p-ignore
            placeholder={inhaber ?? 'Name des Mandanten'}
            value={neuerName}
            onChange={(e) => {
              setzeNeuerName(e.target.value)
              if (e.target.value) setzeGewaehlt(null)
            }}
          />
        </div>

        {karte && kartenKennung && !(vorschlag && vorschlag.mandantId === gewaehlt) && (
          <label className="schalter">
            <input type="checkbox" checked={merken} onChange={(e) => setzeMerken(e.target.checked)} />
            Karte {endeKurz(kartenEnde)} beim Mandanten merken. Künftige Abrechnungen dieser Karte mit demselben Inhaber ordnen sich dann selbst zu.
          </label>
        )}

        {iban && (
          <label className="schalter">
            <input type="checkbox" checked={merken} onChange={(e) => setzeMerken(e.target.checked)} />
            IBAN beim Mandanten merken. Künftige Auszüge dieses Kontos ordnen sich dann selbst zu.
          </label>
        )}

        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={schliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={zuordnen.isPending || (!gewaehlt && !neuerName.trim())}>
            {zuordnen.isPending ? 'Wird zugeordnet …' : 'Zuordnen'}
          </button>
        </div>
      </form>
    </Fenster>
  )
}
